import { ApplicationRef, Injectable, Renderer2 } from '@angular/core';
import { NoNewVersionDetectedEvent, SwUpdate, UnrecoverableStateEvent, VersionDetectedEvent, VersionInstallationFailedEvent, VersionReadyEvent } from '@angular/service-worker';
import { DialogUtility } from '@syncfusion/ej2-angular-popups';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, interval, map } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { APP_VERSION } from '../version';

@Injectable({
  providedIn: 'root',
})
export class SwUpdateService {
  // Was the literal '4.2.5', a third version string that agreed with neither
  // ngsw-config.json nor package.json. See src/app/version.ts.
  readonly initVersion = APP_VERSION;

  // Seeded rather than null so "Version: X" in the options menu shows the real
  // version immediately. It used to be fed ONLY by service-worker events, so it
  // stayed blank forever anywhere the worker is not registered — which is every
  // DEV build (ServiceWorkerModule registers on environment.production, and
  // environment.devTOTP.ts sets that false). A worker event still overwrites
  // this; when the files are in step it writes back the same string.
  appData$ = new BehaviorSubject<any | null>({ version: APP_VERSION });
  version$: Observable<string> = this.appData$.asObservable().pipe(map((data) => data?.version));

  constructor(private swUpdate: SwUpdate, private appRef: ApplicationRef, private toastr: ToastrService, private router: Router) {
    console.log('SwUpdateService', this.swUpdate.isEnabled);
    if (swUpdate.isEnabled) {
      swUpdate.versionUpdates.subscribe((evt) => {
        console.log('SwUpdateService Update', evt);
        switch (evt.type) {
          case 'VERSION_DETECTED':
            this.onVersionDetected(evt);
            break;
          case 'VERSION_READY':
            this.onVersionReady(evt);
            break;
          case 'VERSION_INSTALLATION_FAILED':
            this.onVersionInstallationFail(evt);
            break;
          case 'NO_NEW_VERSION_DETECTED':
            this.onNoNewVersionDetected(evt);
            break;
        }
      });
      swUpdate.unrecoverable.subscribe((event: UnrecoverableStateEvent) => {
        this.openConfirmDialog('Version Unrecoverable', 'The Version is unrecoverable, Please click on "Reload" to fix or please contact support');
      });
      this.swUpdate
        .checkForUpdate()
        .then(() => {
          console.log('Checking update');
        })
        .catch((err) => {
          console.error('Failed to check for updates:', err);
        });
      this.checkUpdate();
    }
  }

  /**
   * Periodic re-check, with the isStable gate REMOVED.
   *
   * The old comment, "remove due to App never Stable somehow", was an accurate
   * observation with the wrong conclusion. concat(appIsStable$, everySixHours$)
   * does not start the interval until appRef.isStable emits true, and this app
   * holds a live SignalR hub, so it never becomes stable and the interval never
   * started. Disabling the call treated the symptom; dropping the gate fixes the
   * cause.
   *
   * Two triggers now, because they cover different users:
   *
   *   interval(6h)   the tab someone leaves open for a working day. Same period
   *                  the original intended; not shortened, because a check is a
   *                  network request and a stale tab is not urgent.
   *   NavigationEnd  the ordinary case. Anyone moving between notebooks, cases
   *                  or settings gets a check within one navigation, which is
   *                  what makes a deploy actually reach an open session.
   *
   * checkForUpdate() only fetches ngsw.json, so a navigation-triggered check is
   * cheap. It resolves false when nothing changed and fires no event.
   */
  checkUpdate() {
    const everySixHours$ = interval(6 * 60 * 60 * 1000);

    everySixHours$.subscribe(() => this.runCheck('interval'));

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.runCheck('navigation'));
  }

  private async runCheck(trigger: string) {
    try {
      const updateFound = await this.swUpdate.checkForUpdate();
      if (updateFound) {
        console.log(`New version available (checked on ${trigger}).`);
      }
    } catch (err) {
      console.error(`Failed to check for updates (${trigger}):`, err);
    }
  }

  private onVersionDetected(evt: VersionDetectedEvent) {
    console.log(`Downloading new app version: ${evt.version.hash}`, evt.version.appData);
  }

  /**
   * PROMPT ON EVERY VERSION_READY. The major-version gate is gone, and it had
   * never once let a prompt through.
   *
   * What it used to do: compare appData.version between the running and the
   * downloaded build, and only prompt if the MAJOR part differed. Three things
   * made that permanently false.
   *
   *   1. appData.version is a single hardcoded string in ngsw-config.json.
   *      `git log -S '"version"' -- ngsw-config.json` returns exactly ONE commit,
   *      22aa36c, 2024-03-13. Nothing in the build bumps it.
   *   2. So both sides always read '4.5.12' and compared equal.
   *   3. getMajorVersion() pops the last segment, turning '4.5.12' into '4.5',
   *      so even a per-deploy patch bump would have been discarded.
   *
   * isMajorUpdate was therefore always false and this dialog has not opened for
   * any user since March 2024. Combined with activateUpdate() appearing NOWHERE
   * in src, a new build reached VERSION_READY and then sat inert until the user
   * happened to fully reload for unrelated reasons.
   *
   * That is a product defect, not a testing annoyance: a production front-end
   * release does not reach a user until they hard-reload by luck.
   *
   * Restoring intended behaviour rather than adding new behaviour. The dialog
   * already existed and was plainly meant to fire.
   */
  private onVersionReady(evt: VersionReadyEvent) {
    console.log('New app version ready.', { current: evt.currentVersion, latest: evt.latestVersion });

    // Deliberately currentVersion, NOT latestVersion, and this differs from the
    // suggested snippet. version$ feeds the "Version: X" line in the options
    // menu, and until the user accepts the reload the app that is RUNNING is
    // still the current one. Showing the latest there would tell them they are
    // on a build they are not yet on. It updates on the next load.
    this.appData$.next(evt.currentVersion.appData);

    this.openConfirmDialog(
      'Update available',
      'A new version is ready. Click Reload to get the latest changes.'
    );
  }

  private onVersionInstallationFail(evt: VersionInstallationFailedEvent) {
    console.error(`Failed to install app version '${evt.version.hash}': ${evt.error}`, evt.version.appData);

    // -------------------------------------------------------------------------
    // PRODUCTION ONLY. The service worker is registered with `enabled: true`
    // unconditionally (app.module.ts), so it runs under `ng serve` too — where
    // the dev server rebuilds on every save and the worker routinely fails to
    // install the version it was mid-way through fetching. That is expected
    // locally and produced a permanent red banner on every reload; it is only
    // meaningful to a real user on a deployed build.
    //
    // The failure is still logged to the console in every environment.
    // -------------------------------------------------------------------------
    if (!environment.production) {
      console.warn('Service worker version install failed — expected during local development. (Toast suppressed outside production.)');
      return;
    }

    this.toastr.error(
      '<p>Application did NOT Load Properly</p><p>If you experience any issues with data updating in the UI after changes, please re-load the application.</p><p>If you continue to experience this issue, please contact support</p>',
      'ERROR',
      {
        enableHtml: true,
        closeButton: true,
        timeOut: 0,
        tapToDismiss: true,
      }
    );
  }

  private onNoNewVersionDetected(evt: NoNewVersionDetectedEvent) {
    console.log(`No new Version updated '${evt.version.hash}`, evt.version);
    this.appData$.next(evt.version.appData);
  }

  private openConfirmDialog(title: string, content: string) {
    DialogUtility.confirm({
      title,
      content,
      okButton: { text: 'Reload', click: this.onReload.bind(this) },
      cancelButton: { text: 'Cancel' },
      showCloseIcon: true,
      closeOnEscape: true,
      animationSettings: { effect: 'Zoom' },
    });
  }

  /**
   * ACTIVATE, THEN reload. activateUpdate() appeared nowhere in src before this,
   * so nothing ever swapped a downloaded version in.
   *
   * A plain reload usually picks the new version up, but "usually" depends on
   * service worker lifecycle timing. Activating first makes it deterministic,
   * which is the whole point of the button.
   *
   * The reload runs even if activation throws, because a user who has clicked
   * Reload must get a reload. Worst case they land on the old version and the
   * prompt appears again, which is the behaviour before this change.
   */
  private async onReload() {
    try {
      await this.swUpdate.activateUpdate();
    } catch (err) {
      console.error('activateUpdate() failed; reloading anyway.', err);
    }
    document?.location?.reload();
  }

  private getMajorVersion(v: string) {
    const version = v.split('.');
    version.pop();
    return version.join('.');
  }

  private compareVersion(v1: string, v2: string) {
    let v1parts = v1.split('.'),
      v2parts = v2.split('.');

    while (v1parts.length < v2parts.length) v1parts.push('0');
    while (v2parts.length < v1parts.length) v2parts.push('0');

    for (var i = 0; i < v1parts.length; ++i) {
      if (v2parts.length == i) {
        return 1;
      }

      if (v1parts[i] == v2parts[i]) {
        continue;
      } else if (v1parts[i] > v2parts[i]) {
        return 1;
      } else {
        return -1;
      }
    }

    if (v1parts.length != v2parts.length) {
      return -1;
    }

    return 0;
  }
}
